// Centralized yield rate calculation. Single source of truth — Jira QS-521.
// Formula:
//   Yield Rate = (actualOutputWeight − fpNotIntactFromLot + fpNotIntactNewGenerated)
//              / (initialRawMaterial − remainderIntact − rawMaterialNotIntact − humanError)
//              × 100
// big.js CJS exports `module.exports = Big` (a function).
// Vite's ESM interop may wrap it as { default: Big }, { Big }, or double-wrap.
// Walk the namespace tree until we find a function (the constructor).
const _Big = require('big.js');
function _pickBig(m) {
  if (m == null) throw new TypeError('big.js export is null/undefined');
  if (typeof m === 'function') return m;
  if (typeof m.default === 'function') return m.default;
  if (typeof m.Big === 'function') return m.Big;
  if (m.default && typeof m.default.default === 'function') return m.default.default;
  if (m.default && typeof m.default.Big === 'function') return m.default.Big;
  throw new TypeError('big.js: cannot resolve Big constructor (keys: ' + Object.keys(m).join(',') + ')');
}
const Big = _pickBig(_Big);

function calculateYieldRate({
  actualOutputWeight,
  fpNotIntactFromLot = 0,
  fpNotIntactNewGenerated = 0,
  initialRawMaterial,
  remainderIntact = 0,
  rawMaterialNotIntact = 0,
  humanError = 0,
} = {}) {
  if (actualOutputWeight == null || initialRawMaterial == null) return 0;

  const denominator = new Big(initialRawMaterial)
    .minus(remainderIntact)
    .minus(rawMaterialNotIntact)
    .minus(humanError);

  if (denominator.lte(0)) return 0;

  const numerator = new Big(actualOutputWeight)
    .minus(fpNotIntactFromLot)
    .plus(fpNotIntactNewGenerated);

  return Number(numerator.div(denominator).times(100).round(2));
}

// Water/liquid loss = the material balance "remainder" after every weighed category.
//   loss = initial − (output − fpNotIntactFromLot) − fpNotIntactNewGenerated − disposable
//          − humanError − byProductTotal − rawMaterialNotIntact − remainderIntact
// fpNotIntactFromLot is finished-product carryover allocated from a prior session's lot:
// it counts toward output but did NOT consume raw material in this session, so it is
// added back to the balance to avoid double-charging the raw material.
// Returns the SIGNED raw value (can be negative ⇒ caller's totals over-allocate).
function calculateWaterLiquidLoss({
  initialRawMaterial,
  actualOutputWeight,
  fpNotIntactFromLot = 0,
  fpNotIntactNewGenerated = 0,
  disposable = 0,
  humanError = 0,
  byProductTotal = 0,
  rawMaterialNotIntact = 0,
  remainderIntact = 0,
} = {}) {
  if (initialRawMaterial == null || actualOutputWeight == null) return 0;

  return Number(new Big(initialRawMaterial)
    .minus(actualOutputWeight)
    .plus(fpNotIntactFromLot)
    .minus(fpNotIntactNewGenerated)
    .minus(disposable)
    .minus(humanError)
    .minus(byProductTotal)
    .minus(rawMaterialNotIntact)
    .minus(remainderIntact)
    .round(2));
}

// Actual waste = categories that count against yield: disposable + humanError + waterLiquid.
function calculateTotalWaste({ disposable = 0, humanError = 0, waterLiquid = 0 } = {}) {
  return Number(new Big(disposable).plus(humanError).plus(waterLiquid).round(2));
}

// Returns to inventory = every category that goes back to a lot.
function calculateTotalReturns({
  remainderIntact = 0,
  rawMaterialNotIntact = 0,
  byProductTotal = 0,
  fpNotIntactNewGenerated = 0,
} = {}) {
  return Number(new Big(remainderIntact)
    .plus(rawMaterialNotIntact)
    .plus(byProductTotal)
    .plus(fpNotIntactNewGenerated)
    .round(2));
}

// Pace = boxes per hour, guarded against tiny elapsed values.
function calculatePace({ boxCount = 0, elapsedHours = 0 } = {}) {
  return elapsedHours > 0.01 ? boxCount / elapsedHours : 0;
}

module.exports = {
  calculateYieldRate,
  calculateWaterLiquidLoss,
  calculateTotalWaste,
  calculateTotalReturns,
  calculatePace,
};
